import { useState, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Send, Smile, PinIcon, Link2, Mic } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatTextWithHashtags } from '@/utils/formatText';
import { timeAgo } from '@/utils/timeAgo';
import type { FeedComment } from '@/types/index';
import type { User } from '@supabase/supabase-js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CommentSectionProps {
  comments: FeedComment[];
  onCommentSubmit: (comment: string) => Promise<void>;
  showInput?: boolean;
  isOwner?: boolean;
  pinnedCommentId?: string;
  onPinComment?: (commentId: string) => Promise<void>;
  showAd?: boolean;
  isPagePost?: boolean;
  user?: User | null;
}

/**
 * Component for displaying comments and comment input
 */
export default function CommentSection({
  comments,
  onCommentSubmit,
  showInput = false,
  isOwner = false,
  pinnedCommentId,
  onPinComment,
  showAd = false,
  isPagePost = false,
  user,
}: CommentSectionProps) {
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commentInputRef = useRef<HTMLInputElement>(null);

  const handleCommentSubmit = async () => {
    if (!commentText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onCommentSubmit(commentText);
      setCommentText('');
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCommentSubmit();
    }
  };

  // Find pinned comment if it exists
  const pinnedComment = pinnedCommentId ? comments.find(comment => comment.id === pinnedCommentId) : undefined;

  // Filter out pinned comment from regular comments
  const regularComments = pinnedCommentId
    ? comments.filter(comment => comment.id !== pinnedCommentId)
    : comments;

  return (
    <div className="px-3 py-2 max-h-full overflow-hidden">
      {/* Pinned Comment Display */}
      {pinnedComment && (
        <div className="mb-4 border-b pb-3 border-gray-200 dark:border-gray-700">
          <div className="flex items-center mb-2 text-xs text-[#065900] font-medium">
            <PinIcon className="h-3 w-3 mr-1" />
            <span>Pinned Comment</span>
          </div>
          <div className="flex items-start gap-2">
            <Link to={`/profile/${pinnedComment.users?.username || 'user'}`} onClick={e => e.stopPropagation()}>
              <Avatar className={`h-7 w-7 ${isPagePost ? 'rounded-full' : 'rounded-lg'}`}>
                <AvatarImage src={pinnedComment.users?.avatar_url} alt={pinnedComment.users?.username || 'user'} />
                <AvatarFallback>{(pinnedComment.users?.username || 'user').slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
            </Link>

            <div className="flex-1">
              <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-3 py-2">
                <div className="flex items-center">
                  <Link
                    to={`/profile/${pinnedComment.users?.username || 'user'}`}
                    className="font-semibold text-xs hover:underline"
                    onClick={e => e.stopPropagation()}
                  >
                    {pinnedComment.users?.username || 'user'}
                  </Link>
                  {pinnedComment.users?.verified && (
                    <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  )}
                </div>
                <span className="text-sm">
                  {formatTextWithHashtags(pinnedComment.content)}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                <span>{timeAgo(pinnedComment.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ad Display */}
      {showAd && (
        <div className="mb-4 border-b pb-3 border-gray-200 dark:border-gray-700">
          <div className="flex items-center mb-2 text-xs text-[#ffc11f] font-medium">
            <span>Sponsored</span>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-md flex items-center justify-center">
                <span className="text-lg">📢</span>
              </div>
              <div>
                <h4 className="font-medium text-sm">Check out our latest features!</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">Upgrade your experience today.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pin Comment Selector - Only visible to post owner */}
      {isOwner && comments.length > 0 && (
        <div className="mb-4">
          <Select onValueChange={(value) => onPinComment && onPinComment(value)} value={pinnedCommentId}>
            <SelectTrigger className="w-full text-xs h-8">
              <SelectValue placeholder="Pin a comment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">No pinned comment</SelectItem>
              {comments.map(comment => (
                <SelectItem key={comment.id} value={comment.id}>
                  {comment.users?.username || 'user'}: {comment.content.substring(0, 30)}{comment.content.length > 30 ? '...' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Regular Comments Display */}
      {regularComments.length > 0 && (
        <div className="mb-3 space-y-2">
          {regularComments.map((comment) => (
            <div key={comment.id} className="flex items-start gap-2">
              <Link to={`/profile/${comment.users?.username || 'user'}`} onClick={e => e.stopPropagation()}>
                <Avatar className={`h-7 w-7 ${isPagePost ? 'rounded-full' : 'rounded-lg'}`}>
                  <AvatarImage src={comment.users?.avatar_url} alt={comment.users?.username || 'user'} />
                  <AvatarFallback>{(comment.users?.username || 'user').slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
              </Link>

              <div className="flex-1">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-3 py-2">
                  <div className="flex items-center">
                    <Link
                      to={`/profile/${comment.users?.username || 'user'}`}
                      className="font-semibold text-xs hover:underline"
                      onClick={e => e.stopPropagation()}
                    >
                      {comment.users?.username || 'user'}
                    </Link>
                    {comment.users?.verified && (
                      <svg className="w-3.5 h-3.5 ml-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm">
                    {formatTextWithHashtags(comment.content)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                  <span>{timeAgo(comment.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comment Input */}
      {showInput && (
        <div className="flex items-center gap-2 mt-2">
          <Avatar className={`h-8 w-8 flex-shrink-0 ${isPagePost ? 'rounded-full' : 'rounded-lg'}`}>
            <AvatarImage src={user?.avatar_url || "/images/avatars/default.jpg"} alt={user?.username || 'user'} />
            <AvatarFallback>{(user?.username || 'user').slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>

          <div className="flex-1 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1">
            <div className="flex-1 flex items-center gap-2">
              <Input
                ref={commentInputRef}
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
              />
              
              <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <Link2 className="h-4 w-4" />
              </button>
              
              <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <Mic className="h-4 w-4" />
              </button>
            </div>

            <button className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              <Smile className="h-5 w-5" />
            </button>

            <button
              onClick={handleCommentSubmit}
              disabled={!commentText.trim() || isSubmitting}
              className={`${commentText.trim() && !isSubmitting
                ? 'text-[#065900] hover:text-[#054800]'
                : 'text-gray-400'
                }`}
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
